import { computed, inject, Injectable } from '@angular/core';
import { AnalysisRowType } from '@api-response-types/analysis-version-group.response';
import { AnalysisDataSourceType } from '@api-types/enums/analysis-data-source-type.enum';
import { AnalysisType } from '@api-types/enums/analysis-type.enum';
import { DateUtilsService } from '@core/utils/date-utils.service';
import { TranslateService } from '@ngx-translate/core';
import { differenceInCalendarMonths } from 'date-fns';
import { AnalysisStateService } from 'demand-forecasting/analysis/services/analysis-state.service';

type MonthCoverage = { covered: number; total: number };

@Injectable({
  providedIn: 'root',
})
export class AnalysisContextService {
  private readonly analysisStateService = inject(AnalysisStateService);
  private readonly dateUtilsService = inject(DateUtilsService);
  private readonly translate = inject(TranslateService);

  readonly predictionSeriesName = computed(() => {
    const version = this.analysisStateService.selectedVersion();
    if (
      version?.dataSourceType === AnalysisDataSourceType.ADDITIONAL_TIMESERIES
    ) {
      return (
        version.additionalTimeseriesName ??
        this.translate.instant('ANALYSIS.PLANNING')
      );
    }
    return this.translate.instant('ANALYSIS.FORECAST');
  });

  /**
   * the anchor cycle's consensus series is measured like a comparison
   * timeseries but is not one of the configured additional timeseries, so it is
   * appended from the version's own snapshot
   */
  readonly comparisonSeriesNames = computed(() => {
    const version = this.analysisStateService.selectedVersion();
    const configured = version?.comparisonTimeseriesNames ?? [];
    const consensus = version?.consensusTimeseriesName;

    return consensus && !configured.includes(consensus)
      ? [...configured, consensus]
      : configured;
  });

  readonly horizon = computed((): string | undefined => {
    const version = this.analysisStateService.selectedVersion();
    if (!version) {
      return undefined;
    }
    return (
      this.dateUtilsService.formatMonthYearBasedOnLanguage(version.minDate) +
      ' - ' +
      this.dateUtilsService.formatMonthYearBasedOnLanguage(version.maxDate)
    );
  });

  readonly timePeriod = computed(
    ():
      | { start: string; end: string; coverage?: MonthCoverage }
      | undefined => {
      const version = this.analysisStateService.selectedVersion();
      if (!version) {
        return undefined;
      }
      if (version.analysisType !== AnalysisType.SINGLE_PERIOD) {
        return { start: version.minDate, end: version.maxDate };
      }

      const versionGroup = this.analysisStateService.aggregatedVersionGroup;
      if (!versionGroup.hasValue()) {
        return undefined;
      }
      const months = versionGroup
        .value()
        .flatMap((row) =>
          row.rowType === AnalysisRowType.TIMEPOINT ? [row.tsDate] : [],
        )
        .sort((a, b) => a.localeCompare(b));

      return {
        start: months.at(0) ?? version.minDate,
        end: months.at(-1) ?? version.maxDate,
        coverage: {
          covered: new Set(months).size,
          total:
            differenceInCalendarMonths(
              new Date(version.maxDate),
              new Date(version.minDate),
            ) + 1,
        },
      };
    },
  );

  readonly timePeriodRange = computed((): string => {
    const period = this.timePeriod();
    if (!period) {
      return '/';
    }
    return `${this.dateUtilsService.formatMonthYearBasedOnLanguage(period.start)} - ${this.dateUtilsService.formatMonthYearBasedOnLanguage(period.end)}`;
  });

  readonly timePeriodValue = computed((): string => {
    const period = this.timePeriod();
    if (!period?.coverage) {
      return this.timePeriodRange();
    }
    return this.translate.instant('ANALYSIS.TIME_PERIOD_VALUE_SINGLE', {
      start: this.dateUtilsService.formatMonthYearBasedOnLanguage(period.start),
      end: this.dateUtilsService.formatMonthYearBasedOnLanguage(period.end),
      n: period.coverage.covered,
      total: period.coverage.total,
    });
  });
}
